/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { UserEdition } from "@encoo-web/encoo-lib";
import { Button, Col, Form, Input, message, Modal, Row, Tabs } from "antd";
import copy from "copy-to-clipboard";
import moment from "moment";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { EnterpriseDetailList } from "src/models/company";
import { AccountSeviceClient } from "src/services/CompanyService";

const formItemColor = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  line-height: 14px;
  color: rgb(61, 73, 102);
  font-weight: 600;
`;
const divColor = css`
  margin-top: 12px;
  color: rgb(143, 155, 179);
  background-color: rgb(247, 249, 252);
  padding: 10px 13px;
  min-height: 32px;
`;

export default React.memo(
  (props: {
    isShowDetail: boolean;
    showDetail: EnterpriseDetailList;
    onClose: () => void;
    fetch: () => void;
  }) => {
    const { isShowDetail, showDetail, onClose, fetch } = props;
    const [form] = Form.useForm();
    const formatMessage = useFormatMessage();
    const [isEdit, setIsEdit] = useState(false);
    const edition = UserEdition[
      showDetail?.edition as UserEdition
    ] as unknown as number;

    useEffect(() => {
      let isUnmounted = false;
      if (!isUnmounted) {
        if (isShowDetail) {
          form.setFieldsValue({
            name: showDetail?.name,
            description: showDetail?.description,
          });
        }
      }
      return () => {
        isUnmounted = true;
      };
    }, [form, isShowDetail, showDetail]);

    const onModalCancel = useCallback(() => {
      onClose();
      setIsEdit(false);
      form.resetFields();
    }, [form, onClose]);

    const onEdit = useCallback(() => {
      setIsEdit(true);
    }, []);

    const onCancel = useCallback(() => {
      setIsEdit(false);
      form.resetFields();
    }, [form]);

    const onSave = useCallback(async () => {
      const formContent = await form.validateFields();
      const formValue = {
        name: formContent.name as string,
      };
      const secondFormValue = {};
      // if (formContent.password) {
      //   secondFormValue = {
      //     ownerUserPassword: formContent.password as string,
      //   };
      // }
      const enterpriseData = Object.assign(formValue, secondFormValue);
      const id = showDetail?.id as string;
      AccountSeviceClient.update({
        id,
        enterpriseData,
      }).then(() => {
        fetch();
        message.success(formatMessage(messageIds.common.updateSuccessfully));
      });
      onModalCancel();
    }, [fetch, form, formatMessage, onModalCancel, showDetail]);

    const onCopy = useCallback(() => {
      copy(showDetail?.id || "");
      message.success(formatMessage(messageIds.common.copySuccessfully));
    }, [formatMessage, showDetail]);

    return (
      <div>
        <Modal
          visible={isShowDetail}
          width="40%"
          onCancel={onModalCancel}
          footer={""}
          css={css`
            top: 60px;
          `}
        >
          <Tabs
            type="card"
            tabBarStyle={{
              height: "30px",
              lineHeight: "30px",
            }}
            css={css`
              .ant-tabs-tab {
                background-color: #84adff !important;
                border-radius: 4px 0px 0px 0px;
                width: 200px;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid #cad1e0;
              }
              .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                color: white;
              }
            `}
          >
            <Tabs.TabPane tab={formatMessage(messageIds.company.setting.name)}>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 10px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.company.setting.title)}
                </h1>
                {isEdit ? (
                  <div>
                    <Button onClick={onCancel}>
                      {formatMessage(messageIds.common.cancel)}
                    </Button>
                    <Button
                      type="primary"
                      css={css`
                        padding: 4px 15px;
                        font-size: 12px;
                        border: 1px solid rgb(51, 119, 255);
                        border-radius: 4px;
                        height: auto;
                        margin-left: 20px;
                      `}
                      onClick={onSave}
                    >
                      {formatMessage(messageIds.common.save)}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Button
                      type="primary"
                      css={css`
                        padding: 4px 15px;
                        font-size: 12px;
                        border: 1px solid rgb(51, 119, 255);
                        border-radius: 4px;
                        height: auto;
                      `}
                      onClick={onEdit}
                    >
                      {formatMessage(messageIds.common.edit)}
                    </Button>
                  </div>
                )}
              </div>
              {
                <Fragment>
                  <Form form={form} layout="vertical">
                    <Row gutter={20}>
                      <Col span={12}>
                        <Form.Item
                          label={
                            <Fragment>
                              <span>
                                {formatMessage(
                                  messageIds.company.setting.customerIdentifier
                                )}
                                :
                              </span>
                              <Button type="link" onClick={onCopy}>
                                {formatMessage(messageIds.common.copy)}
                              </Button>
                            </Fragment>
                          }
                          name="id"
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.id}</div>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.companyType
                          )}：`}
                          css={formItemColor}
                          style={{ paddingTop: "10px" }}
                        >
                          <div css={divColor}>
                            {edition === 0
                              ? formatMessage(messageIds.edition.enterprise)
                              : formatMessage(messageIds.edition.community)}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.companyName
                          )}：`}
                          name="name"
                          css={formItemColor}
                          initialValue={showDetail?.name}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.company.validate.inputCompanyName
                              ),
                            },
                            {
                              max: 256,
                              message: formatMessage(
                                messageIds.company.validate.stringLength,
                                {
                                  name: formatMessage(
                                    messageIds.company.companyName
                                  ),
                                  min: 1,
                                  max: 256,
                                }
                              ),
                            },
                            {
                              whitespace: true,
                            },
                          ]}
                        >
                          {isEdit ? (
                            <Input></Input>
                          ) : (
                            <div css={divColor}>{showDetail?.name}</div>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={20}>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.common.createdBy
                          )}：`}
                          name="createdByName"
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.createdByName}</div>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.common.creationTime
                          )}：`}
                          name="createdAt"
                          css={formItemColor}
                        >
                          <div css={divColor}>
                            {moment(showDetail?.createdAt).format(
                              "YYYY-MM-DD HH:mm:ss"
                            )}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={formatMessage(messageIds.company.companyEmail)}
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.ownerEmail}</div>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={formatMessage(messageIds.company.companySize)}
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.companySize}</div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Fragment>
              }
            </Tabs.TabPane>
          </Tabs>
        </Modal>
      </div>
    );
  }
);
